import { createStore, createEvent, sample, createEffect } from "effector";
import { ModelContract, ModuleDescription } from "../../../shared/module-description";
import { IUserAuthContract, logoutStudent, deleteProfileRequested, analyticsResetFx } from "../model";
import { routerPush } from "@features/shared/router";
import { StudentApi, ILoginEmailStatusResponse, LoginEmailNextAction } from "@features/shared/api/course-data.api";
import { enterCodeScreenCompleted } from "@features/shared/events";
import { AnalyticsService } from "@features/shared/analytics";
import { $isApp, $platform } from "@features/launch-context/model";
import { routePathNameChanged } from "@features/app/model";
import { clearOnboardingSession } from "@features/onboarding/model";
import { getAppOnboardingPath } from "@utils/getAppOnboardingPath";

export enum SignInFlowStep {
    EMAIL = "email",
    PASSWORD = "password",
    CODE = "code"
}

const signInButtonClicked = createEvent();
const signUpButtonClicked = createEvent<void>();
const startOnboardingButtonClicked = createEvent();
export const logoutButtonClicked = createEvent();
const deleteProfileBtnClicked = createEvent();
const emailChangeError = createEvent();
const emailSubmitButtonClicked = createEvent();
const signInStepChanged = createEvent<SignInFlowStep>();

const emailChanged = createEvent<string>();
const passwordChanged = createEvent<string>();
const codeChanged = createEvent<string>();

const codeRequested = createEvent();
const resetPasswordRequested = createEvent();
const emailChangeRequested = createEvent();
const codeChecked = createEvent();
const codeError = createEvent<boolean>();
const forgetPasswordError = createEvent();
const funnelFoxIdChanged = createEvent<string>();
const emailSubmitErrorChanged = createEvent<string>();

const $email = createStore<string>("")
    .on(emailChanged, (s, p) => p)
    .reset(logoutStudent);

const $password = createStore<string>("")
    .on(passwordChanged, (s, p) => p)
    .reset(logoutStudent);

const $code = createStore<string>("")
    .on(codeChanged, (s, p) => p)
    .reset(logoutStudent);

const $codeError = createStore<boolean>(false).on(codeError, (s, p) => p);

const $emailChangeError = createStore<boolean>(false).on(emailChangeError, (s, p) => p);

const $forgetPasswordError = createStore<boolean>(false).on(forgetPasswordError, (s, p) => p);

const $funnelFoxId = createStore<string>("").on(funnelFoxIdChanged, (s, p) => p);
const $signInStep = createStore<SignInFlowStep>(SignInFlowStep.EMAIL).on(signInStepChanged, (_, step) => step);

const $emailSubmitError = createStore<string>("")
    .on(emailSubmitErrorChanged, (_, value) => value)
    .reset(emailChanged, signInStepChanged);

function validate(email: string, password: string) {
    return email.length > 0 && password.length > 0;
}

const EMAIL_NOT_FOUND_MESSAGE = "Email not found";
const GENERIC_EMAIL_ERROR_MESSAGE = "Email not found";

export const codeRequestedFx = createEffect(async (email: string) => {
    try {
        const response = await StudentApi.sendCode(email, false);
        return response;
    } catch (error) {
        throw error;
    }
});
export const resetPasswordRequestedFx = createEffect(async (email: string) => {
    try {
        const response = await StudentApi.sendCode(email, true);
        return response;
    } catch (error) {
        throw error;
    }
});
export const emailChangeRequestedFx = createEffect(async (params: { email: string; funnelFoxId: string }) => {
    try {
        let deviceIdFromStorage: string | null = null;
        if (params.funnelFoxId === "") {
            deviceIdFromStorage = localStorage.getItem("user_uuid");
        }

        const response = await StudentApi.changeEmail({
            email: params.email,
            deviceId: deviceIdFromStorage ?? undefined,
            funnelFoxId: deviceIdFromStorage ? undefined : params.funnelFoxId
        });
        return response;
    } catch (error) {
        throw error;
    }
});

export const signUpAppUserFx = createEffect(async (params: { email: string }) => {
    try {
        const response = await StudentApi.singUpByEmailApp(params);
        return response;
    } catch (error) {
        throw error;
    }
});

export const codeCheckedFx = createEffect(async ({ email, code }: { email: string; code: string }) => {
    try {
        codeError(false);
        const response = await StudentApi.checkCode(email, code);
        return response;
    } catch (error) {
        throw error;
    }
});

const trackEvent = createEffect((data: string | { event: string; properties?: any }) => {
    if (typeof data === "string") {
        AnalyticsService.trackEvent(data);
    } else {
        AnalyticsService.trackEvent(data.event, data.properties);
    }
});

const trackEnterCodeScreen = createEffect(() => enterCodeScreenCompleted());

const loginEmailStatusFx = createEffect(async (email: string) => {
    try {
        const response = await StudentApi.loginEmailStatus(email);
        return response;
    } catch (error) {
        throw error;
    }
});

const $emailSubmitPending = createStore<boolean>(false).on(loginEmailStatusFx.pending, (_, pending) => pending);

// The email belongs to another project's account: its user signs in there.
const otherProjectRedirectFx = createEffect((url: string) => {
    window.location.assign(url);
});

function resolveNextStep(nextAction: LoginEmailNextAction) {
    if (nextAction === "verify_email") {
        return SignInFlowStep.CODE;
    }
    if (nextAction === "enter_password") {
        return SignInFlowStep.PASSWORD;
    }
    return SignInFlowStep.EMAIL;
}

function resolveEmailError(response: ILoginEmailStatusResponse) {
    if (response.status === "not_found") {
        return EMAIL_NOT_FOUND_MESSAGE;
    }
    return "";
}

sample({
    clock: routePathNameChanged,
    filter: (path: string) => path.includes("/app-onboarding"),
    fn: (path: string) => ({
        event: "splash_screen_visit",
        properties: { version: path === "/app-onboarding/v2" ? "v2" : "v1" }
    }),
    target: trackEvent
});

sample({
    source: $email,
    clock: emailSubmitButtonClicked,
    filter: (email) => email.trim().length > 0,
    fn: (email) => email.trim().toLowerCase(),
    target: loginEmailStatusFx
});

sample({
    clock: loginEmailStatusFx.doneData,
    fn: (payload) => resolveNextStep(payload.nextAction),
    target: signInStepChanged
});

sample({
    clock: loginEmailStatusFx.doneData,
    filter: (payload) => payload.nextAction === "error",
    fn: (payload) => resolveEmailError(payload),
    target: emailSubmitErrorChanged
});

sample({
    clock: loginEmailStatusFx.doneData,
    filter: (payload) => payload.nextAction === "redirect" && Boolean(payload.redirectUrl?.startsWith("https://")),
    fn: (payload) => payload.redirectUrl as string,
    target: otherProjectRedirectFx
});

sample({
    clock: loginEmailStatusFx.doneData,
    filter: (payload) => payload.nextAction !== "error",
    fn: () => "",
    target: emailSubmitErrorChanged
});

sample({
    clock: loginEmailStatusFx.failData,
    fn: () => GENERIC_EMAIL_ERROR_MESSAGE,
    target: emailSubmitErrorChanged
});

sample({
    clock: codeRequested,
    source: {
        $email
    },
    fn: (s) => s.$email,
    target: codeRequestedFx
});
sample({
    clock: signUpButtonClicked,
    source: { $email },
    fn: ({ $email }) => ({ email: $email }),
    target: signUpAppUserFx
});
sample({
    clock: signUpAppUserFx.done,
    source: { $email },
    fn: ({ $email }) =>
        `/auth/verify-code?ignore_ff_id=true&email=${encodeURIComponent($email)}&is_app_onboarding=true`,
    target: routerPush
});
sample({
    clock: signUpAppUserFx.done,
    source: {
        $email
    },
    fn: (s) => s.$email,
    target: codeRequestedFx
});
sample({
    clock: signUpAppUserFx.done,
    fn: () => "user_registered",
    target: trackEvent
});

sample({
    clock: resetPasswordRequested,
    source: {
        $email
    },
    fn: (s) => s.$email,
    target: resetPasswordRequestedFx
});

sample({
    clock: emailChangeRequested,
    source: {
        $email,
        $funnelFoxId
    },
    fn: (s) => ({ email: s.$email, funnelFoxId: s.$funnelFoxId }),
    target: emailChangeRequestedFx
});

sample({
    clock: emailChangeRequestedFx.doneData,
    source: {
        $email
    },
    fn: (s) => s.$email,
    target: codeRequestedFx
});

sample({
    clock: emailChangeRequestedFx.doneData,
    source: {
        $email,
        $funnelFoxId
    },
    fn: (s) => `/auth/verify-code?ignore_ff_id=true&email=${encodeURIComponent(s.$email)}`,
    target: routerPush
});

sample({
    clock: emailChangeRequestedFx.fail,
    fn: () => true,
    target: emailChangeError
});

sample({
    clock: codeChecked,
    source: {
        $email,
        $code
    },
    fn: (s) => ({ email: s.$email, code: s.$code }),
    target: codeCheckedFx
});

sample({
    clock: codeCheckedFx.doneData,
    source: { $email, $code },
    fn: (s) => {
        const params = typeof window !== "undefined"
            ? new URLSearchParams(window.location.search)
            : new URLSearchParams();
        const callbackUrl = params.get("callbackUrl");
        let url = `/auth/set-password?email=${encodeURIComponent(s.$email)}&code=${encodeURIComponent(s.$code)}`;
        if (callbackUrl) {
            url += `&callbackUrl=${encodeURIComponent(callbackUrl)}`;
        }
        return url;
    },
    target: [routerPush, trackEnterCodeScreen]
});

sample({
    clock: deleteProfileBtnClicked,
    target: deleteProfileRequested
});

sample({
    source: { $isApp, $platform },
    clock: logoutButtonClicked,
    // analyticsResetFx: explicit logout detaches the analytics person (see model)
    target: [logoutStudent, clearOnboardingSession, analyticsResetFx]
});

sample({
    clock: resetPasswordRequestedFx.doneData,
    source: { $email },
    fn: (s) => {
        const callbackUrl = typeof window !== "undefined"
            ? new URLSearchParams(window.location.search).get("callbackUrl")
            : null;
        let url = `/auth/verify-code?ignore_ff_id=true&email=${encodeURIComponent(s.$email)}&is_reset=true`;
        if (callbackUrl) {
            url += `&callbackUrl=${encodeURIComponent(callbackUrl)}`;
        }
        return url;
    },
    target: routerPush
});

sample({
    clock: resetPasswordRequestedFx.fail,
    fn: () => true,
    target: forgetPasswordError
});

sample({
    clock: codeCheckedFx.fail,
    fn: () => true,
    target: codeError
});

const contract = new ModelContract(
    {
        $email,
        $password,
        $code,
        $codeError,
        $funnelFoxId,
        $forgetPasswordError,
        $emailChangeError,
        $signInStep,
        $emailSubmitError,
        $emailSubmitPending
    },
    {
        emailChanged,
        passwordChanged,
        codeChanged,
        forgetPasswordError,
        signInButtonClicked,
        signUpButtonClicked,
        startOnboardingButtonClicked,
        logoutButtonClicked,
        codeRequested,
        codeChecked,
        funnelFoxIdChanged,
        resetPasswordRequested,
        emailChangeRequested,
        emailChangeError,
        deleteProfileBtnClicked,
        emailSubmitButtonClicked,
        signInStepChanged
    },
    {},
    {}
);

export type ISignInFormContract = typeof contract;

export class SignInFormModule extends ModuleDescription<ISignInFormContract> {
    constructor(public userAuthModule: IUserAuthContract) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            source: {
                $email,
                $password
            },
            clock: signInButtonClicked,
            filter: (s) => validate(s.$email, s.$password),
            fn: (s) => ({
                email: s.$email,
                password: s.$password
            }),
            target: this.userAuthModule.events.signInRequested
        });
    }
}
